import { Button, cn, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Skeleton, Textarea } from '@1trading/react';
import { ExternalLink, Info, UserCircle2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { abuseSubjectPath } from '@/features/abuse/abuse-meta';
import { useAlertComments, useComplianceAlerts } from '@/hooks/queries/compliance/use-compliance-alerts';
import { getErrorMessage } from '@/lib/api/error';
import {
  ALERT_TYPE_TITLES,
  type AlertType,
  addAlertComment,
  type BrokerAlert,
  type ComplianceAlert,
  type FeeSettlementAlert,
  type FuturesReviewAlert,
} from '@/services/api/compliance-alerts';
import { formatDate } from '@/utils/format';
import { formatSnakeCase } from '@/utils/string';
import { BrokerFeeBandReviewActions } from './alert-actions/broker-fee-band-review';
import { BrokerFeeSettlementReviewActions } from './alert-actions/broker-fee-settlement-review';
import { FuturesAppropriatenessReviewActions } from './alert-actions/futures-appropriateness-review';
import { describeAlert } from './alert-descriptions';
import { AlertFieldValue } from './alert-field-value';
import { type BannerVariant, getAbuseCaseSubject, getBannerVariant } from './alert-meta';
import { AlertsTable } from './alerts-table';
import { DismissAlertsButtons, type DismissCopy, useDismissAlerts } from './dismiss-alerts';
import { describeDismissal } from './dismiss-selection';

const BANNER_CLASSES: Record<BannerVariant, string> = {
  destructive: 'border-red-500/50 bg-red-500/10 text-red-500',
  warning: 'border-amber-500/50 bg-amber-500/10 text-amber-600 dark:text-amber-400',
  success: 'border-emerald-500/50 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  info: 'border-blue-500/50 bg-blue-500/10 text-blue-600 dark:text-blue-400',
};

// Alert event types with bespoke action footers; these cannot be plain-dismissed
// or bulk-dismissed from the UI (legacy parity).
const ACTION_EVENT_TYPES = ['broker_fee_band_creation', 'broker_fee_settlement_review', 'broker_fee_settlement_failed', 'futures_appropriateness_manual_review'] as const;

// Stable default: an inline `= []` mints a new array per render and invalidates the memos below.
const NO_ANCESTOR_ALERT_IDS: string[] = [];

interface AlertDetailDialogProps {
  alert: ComplianceAlert;
  alertType: AlertType;
  onClose: () => void;
  onDismissed: () => void;
  /** Alert ids already open in the dialog chain — kept out of "other alerts" to stop A→B→A recursion */
  ancestorAlertIds?: string[];
}

export const AlertDetailDialog = ({ alert, alertType, onClose, onDismissed, ancestorAlertIds = NO_ANCESTOR_ALERT_IDS }: AlertDetailDialogProps) => {
  const comments = useAlertComments(alert.alert_id);
  const otherAlertsQuery = useComplianceAlerts(alertType, alert.pid);
  const { dismissOne, dismissInBulk } = useDismissAlerts(alertType);

  const [selectedOtherAlerts, setSelectedOtherAlerts] = useState<ComplianceAlert[]>([]);
  const [nestedAlert, setNestedAlert] = useState<ComplianceAlert | null>(null);
  const [newComment, setNewComment] = useState('');
  const [isAddingComment, setIsAddingComment] = useState(false);
  const [commentError, setCommentError] = useState<string | null>(null);

  const title = ALERT_TYPE_TITLES[alertType];
  const isOnboarding = alertType === 'onboarding-alert';
  const description = describeAlert(alert.event_type, isOnboarding);
  const isOverdue = typeof alert.days_remaining === 'number' && alert.days_remaining <= 0;
  const abuseSubject = getAbuseCaseSubject(alert);
  const openChainIds = useMemo(() => [...ancestorAlertIds, alert.alert_id], [ancestorAlertIds, alert.alert_id]);
  const otherAlerts = useMemo(() => (otherAlertsQuery.data ?? []).filter((other) => !openChainIds.includes(other.alert_id)), [otherAlertsQuery.data, openChainIds]);

  const hasActionComponent = (ACTION_EVENT_TYPES as readonly string[]).includes(alert.event_type);
  const isBulkDismissAvailable = !hasActionComponent;
  const isBulkDismiss = selectedOtherAlerts.length > 0;
  const dismissTargets = [alert, ...selectedOtherAlerts];
  const eventTypes = Array.from(new Set(dismissTargets.map((target) => target.event_type)));

  const dismissCopy: DismissCopy = {
    noun: isBulkDismiss ? 'Alerts' : 'Alert',
    describe: (muteDuration) =>
      describeDismissal(isBulkDismiss ? `alerts with IDs ${dismissTargets.map((target) => target.alert_id).join(', ')}` : `the alert with ID ${alert.alert_id}`, muteDuration),
    muteTitle: isBulkDismiss ? `Dismiss and Mute ${dismissTargets.length} Alerts` : `Dismiss and Mute Alert: ${alert.alert_id}`,
    renderMuteSummary: (muteDuration) => (
      <p className="text-sm">
        Muting {isBulkDismiss ? 'these alerts' : 'this alert'} means no further alerts for PID <span className="font-medium text-primary">{alert.pid}</span> and alert{' '}
        {eventTypes.length > 1 ? 'types' : 'type'} <span className="font-medium text-primary">{eventTypes.join(', ')}</span> will be generated for {muteDuration}.
      </p>
    ),
  };

  const runDismiss = async (justification: string, muteExpiry?: number) => {
    await (isBulkDismiss ? dismissInBulk(dismissTargets, justification, muteExpiry) : dismissOne(alert, justification, muteExpiry));
    onDismissed();
  };

  // Used by the action components: outcome call already done, now dismiss with the same justification
  const dismissAfterAction = async (justification: string) => {
    await dismissOne(alert, justification);
    onDismissed();
  };

  const handleAddComment = async () => {
    const comment = newComment.trim();
    if (!comment) return;
    setIsAddingComment(true);
    setCommentError(null);
    try {
      await addAlertComment(alert.alert_id, { comment, pid: alert.pid });
      setNewComment('');
      await comments.refetch();
    } catch (error) {
      setCommentError(getErrorMessage(error));
    } finally {
      setIsAddingComment(false);
    }
  };

  return (
    <Dialog onOpenChange={(open) => (open ? undefined : onClose())} open>
      <DialogContent className="flex max-h-[90vh] w-[90vw] flex-col sm:max-w-5xl">
        <DialogHeader>
          <DialogTitle className="break-all">
            Viewing {title}: {alert.alert_id}
          </DialogTitle>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto pr-1">
          <div className="flex items-center justify-between gap-4">
            <h2 className="text-xl font-semibold">{formatSnakeCase(alert.event_type)}</h2>
            {abuseSubject ? (
              <a className="inline-flex items-center gap-1 text-sm underline underline-offset-2" href={abuseSubjectPath(abuseSubject)} rel="noreferrer" target="_blank">
                View in Abuse Monitoring
                <ExternalLink className="size-3.5" />
              </a>
            ) : null}
          </div>

          {description ? (
            <div className={cn('flex gap-3 rounded-md border-l-4 border p-4 text-sm leading-relaxed', BANNER_CLASSES[getBannerVariant(alert.severity, isOverdue)])}>
              <Info className="mt-0.5 size-5 shrink-0" />
              <div>
                {isOverdue ? <p className="mb-1 font-semibold">THIS ALERT IS OVERDUE ({Math.abs(Math.round(alert.days_remaining ?? 0))} DAYS)</p> : null}
                <p>{description}</p>
              </div>
            </div>
          ) : null}

          <section>
            <h3 className="mb-2 font-semibold">Raw data for {title.toLowerCase()}</h3>
            <div className="overflow-hidden rounded-md border">
              <table className="w-full text-sm">
                <tbody>
                  {Object.entries(alert).map(([field, value]) => (
                    <tr className="border-b last:border-b-0" key={field}>
                      <td className="w-52 border-r bg-muted/30 px-3 py-2 align-top font-mono text-xs">{field}</td>
                      <td className="px-3 py-2">
                        <AlertFieldValue alert={alert} field={field} value={value} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {otherAlerts.length > 0 ? (
            <section>
              <h3 className="mb-2 font-semibold">Other Alerts for this User ({otherAlerts.length})</h3>
              <AlertsTable
                alerts={otherAlerts}
                alertType={alertType}
                isLoading={false}
                onRowClick={setNestedAlert}
                onSelectionChange={setSelectedOtherAlerts}
                selectable={isBulkDismissAvailable}
              />
            </section>
          ) : null}

          <section>
            <h3 className="mb-2 font-semibold">Comments</h3>
            <div className="flex flex-col gap-2">
              <Textarea onChange={(event) => setNewComment(event.target.value)} placeholder="Add a comment" value={newComment} />
              {commentError ? <p className="text-sm text-red-500">{commentError}</p> : null}
              <div className="flex justify-end">
                <Button disabled={!newComment.trim() || isAddingComment} onClick={handleAddComment} type="button">
                  Add Comment
                </Button>
              </div>
            </div>
            <div className="mt-3 flex flex-col gap-3">
              {comments.isPending ? <Skeleton className="h-16 w-full" /> : null}
              {comments.isError ? <p className="text-sm text-red-500">Failed to load comments: {getErrorMessage(comments.error)}</p> : null}
              {comments.data?.length === 0 ? <p className="text-sm text-muted-foreground">No comments available.</p> : null}
              {comments.data?.map((comment) => (
                <div className="flex gap-2" key={comment.comment_id}>
                  <UserCircle2 className="mt-0.5 size-5 shrink-0 text-muted-foreground" />
                  <div className="min-w-0">
                    <p className="text-sm">
                      <strong>{comment.commented_by}</strong> <span className="text-muted-foreground">{formatDate(comment.commented_at)}</span>
                    </p>
                    <p className="break-words text-sm">{comment.comment}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>

        <DialogFooter>
          {alert.event_type === 'broker_fee_band_creation' ? (
            <BrokerFeeBandReviewActions alert={alert as BrokerAlert} onClose={onClose} onDismiss={dismissAfterAction} />
          ) : alert.event_type === 'broker_fee_settlement_review' || alert.event_type === 'broker_fee_settlement_failed' ? (
            <BrokerFeeSettlementReviewActions alert={alert as FeeSettlementAlert} onClose={onClose} onDismiss={dismissAfterAction} />
          ) : alert.event_type === 'futures_appropriateness_manual_review' ? (
            <FuturesAppropriatenessReviewActions alert={alert as FuturesReviewAlert} onClose={onClose} onDismiss={dismissAfterAction} />
          ) : (
            <div className="flex justify-end gap-2">
              <Button onClick={onClose} type="button" variant="outline">
                Close
              </Button>
              <DismissAlertsButtons copy={dismissCopy} onDismiss={runDismiss} />
            </div>
          )}
        </DialogFooter>

        {/* Nested dialog for an "other alert" row */}
        {nestedAlert ? (
          <AlertDetailDialog
            alert={nestedAlert}
            alertType={alertType}
            ancestorAlertIds={openChainIds}
            onClose={() => setNestedAlert(null)}
            onDismissed={() => setNestedAlert(null)}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
};
AlertDetailDialog.displayName = 'AlertDetailDialog';
